import { Caption } from '~/shared/Typography';
import {
    HStack,
    Pressable,
    VStack,
} from 'native-base';
import { GiftCardsDTO, useSpringBigStore, useTheme } from '@springbig/shared';
import React, { useState } from 'react';
import PaginationDot from 'react-native-animated-pagination-dot';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, Pagination, Keyboard } from 'swiper';
import Barcode from 'react-barcode';
import { SpIcon } from '~/shared/Icon';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useAppNavigate } from '~/pages';
import { Button, Card } from '~/shared/Theme';

type GiftCardsProps = {
    data: GiftCardsDTO;
};

export const GiftCards = ({
    data,
}: GiftCardsProps) => {

    const { navLogo, merchant, } = useSpringBigStore();
    const { navigate, params } = useAppNavigate();

    const {
        primary,
        button_primary,
        button_text,
        radius_md,
    } = useTheme();

    const [showBalance, setShowBalance] =
        useState<boolean>(false);

    const [activeSlide, setActiveSlide] =
        useState<number>(0);

    const [isShowBarcodes, setIsShowBarcodes] =
        useState<boolean>(false);

    const onToggleBarCode = () => {

        setActiveSlide(0);
        setIsShowBarcodes(!isShowBarcodes);
    };

    if (isShowBarcodes) {
        return (
            <Card p={0} hasShadow>
                <Swiper
                    spaceBetween={8}
                    navigation={false}
                    keyboard={true}
                    modules={[Pagination, Mousewheel, Keyboard]}
                    className="mySwiper"
                    style={{
                        pointerEvents: 'auto',
                        width: '100%',
                    }}
                    onSlideChange={(swiper) => {

                        setActiveSlide(swiper.snapIndex);
                    }}
                >
                    { data.cards
                        .sort((a, b) => b.primary - a.primary)
                        .map((card, index) => (

                            <SwiperSlide
                                style={{
                                    width: '100%',
                                    height: '100%',
                                }}
                                key={index}
                            >
                                <Card
                                    alignItems={'center'}
                                    bg={button_primary}
                                    space={2}
                                >
                                    <HStack
                                        justifyContent={'space-around'}
                                        alignItems={'baseline'}
                                        space={4}
                                    >
                                        <Caption
                                            text={'home_page.gift_cards.card_balance'}
                                            textValues={[card.balance.toFixed(2)]}
                                        />
                                        { card.primary &&
                                            <Caption text={'giftCard.primary.title'} />
                                        }
                                    </HStack>
                                    <Barcode
                                        value={card.card_number}
                                        format={'CODE128'}
                                        background={'white'}
                                    />
                                    <PaginationDot
                                        activeDotColor={button_text}
                                        curPage={activeSlide}
                                        maxPage={data.cards ? data.cards.length : 1}
                                    />
                                </Card>
                            </SwiperSlide>
                        ))}
                </Swiper>
                <Button
                    text={'home_page.gift_cards.back'}
                    variant={'text'}
                    onPress={onToggleBarCode}
                />
            </Card>
        );
    }

    return (
        <Card p={0} hasShadow>
            <Swiper
                spaceBetween={8}
                navigation={false}
                keyboard={true}
                modules={[Pagination, Mousewheel, Keyboard]}
                className="mySwiper"
                style={{
                    pointerEvents: 'auto',
                    width: '100%',
                }}
                onSlideChange={(swiper) => {

                    setActiveSlide(swiper.snapIndex);
                }}
            >
                { data.cards
                    .sort((a, b) => b.primary - a.primary)
                    .map((card, index) => (

                        <SwiperSlide
                            style={{
                                width: '100%',
                                height: '100%',
                            }}
                            key={index}
                        >
                            <VStack
                                bg={button_primary}
                                rounded={radius_md}
                                shadow={2}
                                space={8}
                                paddingY={2}
                                paddingX={4}
                            >
                                <HStack justifyContent={'space-between'} alignItems={'center'}>
                                    <Caption fontSize={'xl'} bold color={button_text}>
                                        { merchant?.gift_card_alias }
                                    </Caption>
                                    <img
                                        className="nav--logo"
                                        src={navLogo}
                                        alt="logo"
                                        width={40}
                                        style={{ alignSelf: 'flex-end', justifySelf: 'flex-end' }}
                                    />
                                </HStack>
                                <HStack alignItems={'center'} space={4}>
                                    <Pressable onPress={() => setShowBalance(!showBalance)}>
                                        <HStack alignItems="center" space={1}>
                                            <SpIcon
                                                icon={showBalance ? faEyeSlash : faEye}
                                                color={button_text}
                                                size={16}
                                            />
                                            <Caption
                                                text={showBalance ? 'homepage.giftCard.hide' : 'homepage.giftCard.show'}
                                                color={button_text}
                                                underline
                                            />
                                        </HStack>
                                    </Pressable>
                                    <HStack alignItems={'space-between'} flex={1}>
                                        {
                                            showBalance ?
                                                card.card_number
                                                    .split('')
                                                    .map((digit, _index) => (

                                                        <Caption
                                                            key={_index}
                                                            textAlign={'right'}
                                                            color={button_text}
                                                            flex={1}
                                                        >
                                                            { digit }
                                                        </Caption>
                                                    ))
                                                :
                                                card.card_number.split('').map((_digit, __index) => (

                                                    <Caption
                                                        key={__index}
                                                        textAlign={'right'}
                                                        color={button_text}
                                                        flex={1}
                                                    >
                                                        { __index < card.card_number.length - 4 ? '*' : _digit }
                                                    </Caption>
                                                ))
                                        }
                                    </HStack>
                                </HStack>

                                <HStack justifyContent="space-between">
                                    <VStack>
                                        <Caption
                                            text={'homepage.giftCard.pin'}
                                            color={button_text}
                                        />
                                        <Caption color={button_text} fontSize={11}>
                                            { showBalance ? card.card_pin : '****' }
                                        </Caption>
                                    </VStack>
                                    <VStack>
                                        <Caption
                                            text={'homepage.giftCard.balance'}
                                            color={button_text}
                                            textAlign={'right'}
                                        />
                                        <Caption fontSize={'2xl'} bold color={button_text}>
                                            ${ card.balance.toFixed(2) }
                                        </Caption>
                                    </VStack>
                                </HStack>
                            </VStack>
                            { data.cards.length > 1 &&
                                <VStack alignItems={'center'} pt={2}>
                                    <PaginationDot
                                        activeDotColor={primary}
                                        curPage={activeSlide}
                                        maxPage={data.cards ? data.cards.length : 1}
                                    />
                                </VStack>
                            }
                        </SwiperSlide>
                    ))}
            </Swiper>

            <HStack p={4} pt={2} flex={1} space={4} justifyContent={'center'}>
                <Button
                    flex={1}
                    onPress={() => navigate(`w/${params.wallet_id}/giftCard`)}
                    text={'homepage.giftCard.manageFunds'}
                    variant="neutral"
                />
                <Button
                    onPress={onToggleBarCode}
                    flex={1}
                    text={'homepage.giftCard.viewBarcode'}
                    variant="neutral"
                />
            </HStack>
        </Card>
    );
};
